/**
 * Что вилка даёт ИМЕННО НА ВАШ БАНК, а не по лучшей цене.
 *
 * Главное число ленты считается по вершине стакана. Оно честное ровно для той
 * суммы, которая по этой вершине влезает, — а на киберспортивном Polymarket по
 * лучшей цене влезает медиана 13 долларов (замер в docs/kb). Человек видит
 * «22.07%», ставит 500 и получает другой ROI, потому что вторая нога съедает
 * уровни глубже. Конкурент показывает одно число `profit` и молчит про глубину —
 * это и есть главная ложь сканеров.
 *
 * Сервер уже прошёл по стакану и прислал лестницу `vol` (ROI при 100, 250, 500)
 * и `maxVol` — максимум, при котором вилка ещё в плюс. Здесь эта лестница
 * превращается в один приговор для банка пользователя.
 *
 * Decimal, а не number: те же деньги, что в calc.ts, и то же правило — сравнение
 * банка с порогом не должно зависеть от того, как сложились двоичные дроби.
 */

import { Decimal } from 'decimal.js';

import type { Fork, VolumeStep } from './protocol';

export interface BankVerdict {
  /** ROI при этом банке, проценты строкой. null — лестницы нет, врать нечем. */
  roi: string | null;
  /** Банк измеренной ступени, по которой взят ROI. */
  step: string | null;
  /**
   * Банк больше последней измеренной ступени: показанный ROI — оценка сверху.
   * Дальше по стакану будет только хуже, и сказать это надо словами.
   */
  beyondLadder: boolean;
  /** Сколько реально влезает в плюс. null — сервер не сказал. */
  max: string | null;
  /** Банк не влезает: ставить эту сумму значит выйти из плюса. */
  over: boolean;
  /** На сколько процентных пунктов ROI под банк ниже заголовочного. */
  drop: Decimal | null;
}

const NOTHING: BankVerdict = {
  roi: null,
  step: null,
  beyondLadder: false,
  max: null,
  over: false,
  drop: null,
};

/** Число из строки протокола. Нечитаемое — null: подставленный ноль тут врёт. */
function num(value: unknown): Decimal | null {
  if (typeof value !== 'string' && typeof value !== 'number') return null;
  try {
    const parsed = new Decimal(value);
    return parsed.isFinite() ? parsed : null;
  } catch {
    return null;
  }
}

/** Ступени по возрастанию банка. Порядок сервера не гарантирован протоколом. */
function ladder(steps: VolumeStep[]): { bank: Decimal; roi: string }[] {
  return steps
    .map((step) => ({ bank: num(step.bank), roi: step.roi }))
    .filter((step): step is { bank: Decimal; roi: string } => step.bank !== null)
    .sort((a, b) => a.bank.comparedTo(b.bank));
}

/**
 * Приговор для банка: какой ROI и влезает ли вообще.
 *
 * Ступень выбирается ПЕРВАЯ, чей банк не меньше запрошенного, — то есть замер,
 * который эту сумму уже покрывает. Между 250 и 500 честнее показать 500: ROI по
 * стакану убывает, и ошибиться здесь можно только в одну сторону — в сторону
 * завышения, а завышенный ROI это ставка, которой человек не сделал бы.
 * Интерполяции нет намеренно: середина между двумя замерами — это выдуманное
 * число, а выдуманные числа в ленте неотличимы от измеренных.
 */
export function bankVerdict(fork: Fork, bank: number | string): BankVerdict {
  const want = num(bank);
  if (want === null || want.lte(0)) return NOTHING;

  const max = num(fork.maxVol);
  const over = max !== null && want.gt(max);
  const steps = ladder(fork.vol ?? []);
  if (steps.length === 0) {
    return { ...NOTHING, max: fork.maxVol ?? null, over };
  }

  const covering = steps.find((step) => step.bank.gte(want));
  const step = covering ?? steps[steps.length - 1]!;
  const headline = num(fork.roi);
  const atBank = num(step.roi);

  return {
    roi: step.roi,
    step: step.bank.toString(),
    beyondLadder: covering === undefined,
    max: fork.maxVol ?? null,
    over,
    drop: headline !== null && atBank !== null ? headline.minus(atBank) : null,
  };
}

/**
 * Строка объёма для ленты: одна короткая фраза вместо голого «≤5550$».
 *
 * Порядок ветвлений — по цене ошибки. Сначала «не влезает»: банк, при котором
 * вилка уходит в минус, важнее любых процентов. Потом просевший ROI. И только
 * если ни того, ни другого — сумма, которая влезает.
 */
export function volumeText(verdict: BankVerdict, bank: number | string): string {
  if (verdict.over) return `влезает ${verdict.max}$`;
  if (verdict.roi === null) return verdict.max === null ? '—' : `≤${verdict.max}$`;
  const tail = verdict.beyondLadder ? ' и ниже' : '';
  return `${bank}$ → ${verdict.roi}%${tail}`;
}

/** Подсказка с полной лестницей: в строке её негде показать, а сверить надо. */
export function volumeHint(fork: Fork, verdict: BankVerdict): string {
  const steps = (fork.vol ?? []).map((step) => `${step.bank}$ → ${step.roi}%`).join(', ');
  const bits = [steps ? `проход по стакану: ${steps}` : 'лестницы объёма нет'];
  if (verdict.max !== null) bits.push(`максимум в плюс ${verdict.max}$`);
  if (verdict.beyondLadder) bits.push('ваш банк больше последнего замера, ROI будет ниже');
  if ((fork.limits ?? []).length > 0) bits.push(`упирается в: ${(fork.limits ?? []).join('; ')}`);
  return bits.join('. ');
}

/** Насколько тревожно показывать этот объём. Порог просадки — половина ROI. */
export function volumeTone(verdict: BankVerdict): 'bad' | 'warn' | 'plain' {
  if (verdict.over) return 'bad';
  if (verdict.drop === null || verdict.roi === null) return 'plain';
  const atBank = num(verdict.roi);
  if (atBank === null) return 'plain';
  // Просадка сравнивается с самим ROI, а не с абсолютным порогом: потерять
  // половину от 20% и половину от 1% одинаково меняют решение о ставке.
  return verdict.drop.gt(atBank) ? 'warn' : 'plain';
}

/** Влезает ли банк — для фильтра «не показывать мелочь». */
export function fitsAtLeast(fork: Fork, minBank: number): boolean {
  if (minBank <= 0) return true;
  const max = num(fork.maxVol);
  // Сервер не сказал максимум — не наше дело выкидывать вилку из ленты по
  // молчанию: пропущенная вилка стоит столько же, сколько показанная мусорная.
  return max === null || max.gte(minBank);
}
